/**
 * Whether the shell is below its rail breakpoint — a phone's width (D224).
 *
 * @remarks
 * The stylesheet draws the shell two ways, and the line between them is one
 * media query: below it there is no rail, the masthead is inline, and (Mike,
 * 2026-10-01) the map names only the rooms walked, the one the player
 * stands in at full size. The scene answers that in CSS; the hologram that
 * stands in for it where WebGL is missing cannot, because its labels are
 * settled by the engine (D174) and a stylesheet cannot take a `???` away
 * without leaving the lift it caused. So the shell asks the same query the
 * stylesheet does and hands the answer to the layout. One query, read here
 * and in `styles.css`; change one and change the other.
 *
 * jsdom ships no `matchMedia`; there, the shell is wide.
 *
 * @packageDocumentation
 */

import { useSyncExternalStore } from 'react';

/** The stylesheet's rail breakpoint, verbatim: `@media (max-width: 47.999rem)` in `styles.css`. */
export const RAIL_BREAKPOINT_QUERY = '(max-width: 47.999rem)';

/** The media query list, or nothing where the environment has no media queries. */
const railBreakpointQueryOf = (): MediaQueryList | undefined =>
	typeof window.matchMedia === 'function' ? window.matchMedia(RAIL_BREAKPOINT_QUERY) : undefined;

const subscribe = (onChange: () => void): (() => void) => {
	const query = railBreakpointQueryOf();

	query?.addEventListener('change', onChange);

	return () => {
		query?.removeEventListener('change', onChange);
	};
};

const getSnapshot = (): boolean => railBreakpointQueryOf()?.matches ?? false;

/**
 * Whether the shell is currently below the rail breakpoint, and re-rendered
 * when that changes — a phone turned, a window narrowed.
 *
 * @returns True at a phone's width
 */
export const useIsBelowRailBreakpoint = (): boolean => useSyncExternalStore(subscribe, getSnapshot);
